import type { BlockEmbed } from 'quill/blots/block';
import React from 'react';
import { MenuItem } from '../../../components/Menu';
import newTabIcon from '../../../icons/editor/newTab.svg';
import Video from '../../formats/Video';
import type { HoveredBlotListenerSetting } from '../../HoveredBlotListener';
import { useHoveredBlotListener } from '../../HoveredBlotListener';
import { useQuill } from '../../QuillContext';
import { getFullUrl } from '../../utils/matchVideo';
import DisplayAsLinkMenuItem from '../DisplayAsLinkMenuItem';
import { ToolbarButtonGroup } from './buttons/ToolbarButton';
import ToolbarCommentButtonGroup from './buttons/ToolbarCommentButtonGroup';
import ToolbarEditButton from './buttons/ToolbarEditButton';
import ToolbarOverflowMenuButton from './buttons/ToolbarOverflowMenuButton';
import FloatingToolbar from './FloatingToolbar';
import formatBlotWithoutScrolling from './formatBlotWithoutScrolling';
import useBlotHighlightedClass from './useBlotHighlightedClass';
import ToolbarSpanButtonGroup from './buttons/ToolbarSpanButtonGroup';

type Formats = Record<string, string[] | string | null | undefined> & {
  comment?: string[] | null;
};

interface BaseVideoToolbarProps {
  hoveredBlot: BlockEmbed;
  formats: Formats;
  format: (format: { height?: string | null; span?: string | null }) => void;
  setPersist: (persist: boolean) => void;
  value: string;
  readOnly?: boolean;
}

export const BaseVideoToolbar = ({
  hoveredBlot,
  formats,
  format,
  setPersist,
  value,
  readOnly,
}: BaseVideoToolbarProps) => {
  const { quill } = useQuill();
  useBlotHighlightedClass(hoveredBlot);

  return (
    <>
      {readOnly ? (
        <ToolbarButtonGroup>
          <ToolbarEditButton targetBlot={hoveredBlot} />
        </ToolbarButtonGroup>
      ) : (
        <ToolbarSpanButtonGroup
          span={formats.span}
          onSpanChange={(newSpan) => format({ span: newSpan })}
        />
      )}
      <ToolbarCommentButtonGroup
        blot={hoveredBlot}
        existingComment={formats.comment}
        onReactionPickerOpenChange={setPersist}
      />
      <ToolbarButtonGroup>
        <ToolbarOverflowMenuButton onMenuOpenChange={setPersist}>
          {(onClose) => (
            <>
              <MenuItem
                icon={newTabIcon}
                to={value}
                target="_blank"
                onClick={onClose}
              >
                Open in new tab
              </MenuItem>
              {!readOnly && quill && (
                <DisplayAsLinkMenuItem
                  quill={quill}
                  blot={hoveredBlot}
                  link={getFullUrl(value)}
                  onClick={onClose}
                />
              )}
            </>
          )}
        </ToolbarOverflowMenuButton>
      </ToolbarButtonGroup>
    </>
  );
};

type Context = {
  formats: Formats;
  value: string;
};

const hoveredBlotListenerSetting: HoveredBlotListenerSetting<Context> = {
  getHoveredBlot(blot) {
    return blot.statics.blotName === Video.blotName ? blot : null;
  },
  getContext(blot: Video) {
    return {
      formats: blot.formats(),
      value: blot.value().video,
    };
  },
};

const VideoToolbar = () => {
  const { quill, readOnly } = useQuill();

  const {
    bounds,
    hoveredBlot,
    context: { formats, value } = { formats: undefined, value: undefined },
    setPersist,
  } = useHoveredBlotListener(quill, hoveredBlotListenerSetting);

  return (
    <FloatingToolbar bounds={bounds}>
      {hoveredBlot instanceof Video && formats && value && (
        <BaseVideoToolbar
          hoveredBlot={hoveredBlot}
          formats={formats}
          format={(attributes) => {
            if (!quill) return;

            formatBlotWithoutScrolling({
              quill,
              attributes,
              blot: hoveredBlot,
            });
          }}
          setPersist={setPersist}
          value={value}
          readOnly={readOnly}
        />
      )}
    </FloatingToolbar>
  );
};

export default VideoToolbar;
